import { useSkillBookState } from "@pages/Contents/BadgeAndCertificates/context/SkillBookContext";
import ReplacedCourseField from "@pages/Contents/BadgeAndCertificates/components/ReplacedCourseField";
import { Step2Props } from "@pages/Contents/BadgeAndCertificates/types";
import InputCst from "@components/InputCst/InputCst";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  List,
  ListItemText,
  ListItem,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { useTheme } from "@mui/material/styles";
import SelectCst from "@components/InputCst/SelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import { VisuallyHiddenInput } from "@components/InputCst/UploadInput";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useEffect, useState } from "react";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import {
  bytesToMB,
  handleDownloadFile,
  handleFileChange,
  handleMultiFileChange,
  handleMultiFileDelete,
} from "@pages/Contents/BadgeAndCertificates/utils/utils";
import InputGroup from "@components/InputCst/InputGroup";
import { useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const MAX_SIZE_UPLOAD = 10 * 1024 * 1024; // 10 Mb

const Step2: React.FC<Step2Props> = ({
  columns,
  errors,
  student_id,
  userIds,
}) => {
  const { t } = useTranslation("skillBook-page");
  const theme = useTheme();
  const { state, dispatch } = useSkillBookState();
  const { id } = useParams();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [knowledgeArea, setKnowledgeArea] = useState([]);
  const [knowledgeDomain, setKnowledgeDomain] = useState([]);
  const [selectedArea, setSelectedArea] = useState<{
    value?: string | number;
  }>({});

  const { dataTable: dataKnowledgeDomains } = useFetchTable(
    // GET DOMAINS OPTIONS
    `${ServicesURL.getKnowledgeDomains
      .replace(":idCorporate", `${id}`)
      .replace(":idKnowledgeArea", selectedArea.value?.toString() || "1")}`,
    PagesID["skills.knowledge-domains"],
    { pageIndex: 0, pageSize: 100 },
  );

  const { dataTable: dataKnowledgeAreas } = useFetchTable(
    // GET AREAS OPTIONS
    `${ServicesURL.getKnowledgeAreas.replace(":idCorporate", `${id}`)}`,
    PagesID["skills.knowledge-areas"],
    { pageIndex: 0, pageSize: 100 },
  );

  const { dataTable: capabilitiesOptions } = useFetchTable(
    //GET CAPABILITIES OPTIONS
    `${ServicesURL.getCapabilities.replace(":idCorporate", `${id}`)}`,
    PagesID["skills.capabilities"],
  );

  const campaignsUrl = student_id
    ? `${ServicesURL.findCampaignsAdmin.replace(":corporateId", String(id))}?student_id=${encodeURIComponent(student_id)}`
    : null;

  const { dataTable: campaignsData } = useFetchTable(
    campaignsUrl,
    PagesID["skills.collect-campaigns"],
    { pageIndex: 0, pageSize: 100 },
  );

  const commonCampaignsUrl =
    userIds && userIds.length > 1
      ? `${ServicesURL.findCommonCampaignsAdmin.replace(":corporateId", String(id))}?${userIds.map((uid) => `user_ids=${uid}`).join("&")}`
      : null;

  const { dataTable: commonCampaignsData } = useFetchTable(
    commonCampaignsUrl,
    PagesID["skills.collect-campaigns"],
    { pageIndex: 0, pageSize: 100 },
  );

  useEffect(() => {
    const source =
      userIds && userIds.length > 1 ? commonCampaignsData : campaignsData;
    if (!source) return;
    const raw: any[] = Array.isArray(source) ? source : source.output || [];
    setCampaignsOptions(
      raw.map((c) => ({
        label: c.name,
        value: c.id,
      })),
    );
  }, [campaignsData, commonCampaignsData, userIds]);

  const [campaignsOptions, setCampaignsOptions] = useState<
    { label: string; value: number }[]
  >([]);

  useEffect(() => {
    if (!campaignsData) return;
    // se campaignsData ha .output altrimenti è già array
    const raw: any[] = Array.isArray(campaignsData)
      ? campaignsData
      : campaignsData.output || [];
    setCampaignsOptions(
      raw.map((c) => ({
        label: c.name,
        value: c.id,
      })),
    );
  }, [campaignsData]);
  const { dataTable: trainingTypeOptions } = useFetchTable(
    `${ServicesURL.getCourseType}`,
    PagesID["manage-requests"],
  );

  const handleFieldChange = (field: string, value: any) => {
    dispatch({
      type: "SET_INPUT_VALUES",
      payload: { key: field, value },
    });
  };

  const handleChangeMultiSelect = (
    event: { label: string; value: string }[],
    accessorKey: string,
  ) => {
    let newValues: any[] = [];

    if (!!state[accessorKey]) {
      const itemsToAdd = event.filter(
        (ev) =>
          !state[accessorKey].some((area: any) => area.value === ev.value),
      );
      const itemsToRemove = state[accessorKey].filter(
        (area: any) => !event.some((ev) => ev.value === area.value),
      );

      newValues = [
        ...state[accessorKey].filter(
          (area: any) => !itemsToRemove.includes(area),
        ),
        ...itemsToAdd,
      ];
    } else {
      newValues = [...event];
    }

    dispatch({
      type: "SET_INPUT_VALUES",
      payload: { key: accessorKey, value: newValues },
    });
  };

  useEffect(() => {
    if (!dataKnowledgeAreas?.output) return;
    setKnowledgeArea(
      dataKnowledgeAreas.output.map((item: any) => ({
        label: item.name,
        value: item.id,
      })),
    );
  }, [dataKnowledgeAreas]);

  useEffect(() => {
    if (!dataKnowledgeDomains?.output) return;

    const newKnowledgeDomain = dataKnowledgeDomains.output.map((item: any) => ({
      label: item.name,
      value: item.id,
    }));

    setKnowledgeDomain(newKnowledgeDomain);
  }, [dataKnowledgeDomains]);

  const getOptions = (type: string) => {
    switch (type) {
      case "courseType":
        return trainingTypeOptions || [];
      case "knowledge_area":
        return knowledgeArea || [];
      case "knowledge_domain":
        return knowledgeDomain || [];
      case "capability":
        if (!capabilitiesOptions?.output) return [];
        return capabilitiesOptions.output.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
      case "campaign":
        return campaignsOptions;
      default:
        return [];
    }
  };

  return (
    <>
      <Grid container>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: isSmallScreen ? 0 : "7px",
            minHeight: "495px",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Grid container spacing={3}>
            {columns &&
              columns.map((col: any) => {
                switch (col.type) {
                  case "date":
                    return (
                      <Grid key={col.accessorKey} item xs={12} sm={6}>
                        <DataPickerCst
                          id={col.accessorKey}
                          label={col.header}
                          value={state[col.accessorKey] ?? ""}
                          onChange={(e: any) => {
                            const date = new Date(e.$d);
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(
                              2,
                              "0",
                            ); // Mesi da 0 a 11
                            const day = String(date.getDate()).padStart(2, "0");
                            const formattedDate = `${year}-${month}-${day}`;
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: col.accessorKey,
                                value: formattedDate,
                              },
                            });
                          }}
                          required={col.required}
                          error={!!errors[col.accessorKey]}
                          errorMessage={`${t(
                            `validation.${errors[col.accessorKey]}`,
                          )}`}
                        />
                      </Grid>
                    );
                  case "text":
                    return (
                      <Grid key={col.accessorKey} item xs={12} sm={6}>
                        <InputCst
                          key={col.accessorKey}
                          id={col.accessorKey}
                          label={col.header}
                          value={state[col.accessorKey]}
                          required={col.required}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: col.accessorKey,
                                value: e.target.value,
                              },
                            });
                          }}
                          maxValue={col?.rules?.max?.value}
                          multiline={col.multiline}
                          rows={col.rows}
                          disabled={col.disabled}
                          textTooltip={col.tooltip}
                          type={col.inpuType || "text"}
                          error={!!errors[col.accessorKey]}
                          errorMessage={`${t(
                            `validation.${errors[col.accessorKey]}`,
                          )}`}
                        />
                      </Grid>
                    );
                  case "single_text":
                    return (
                      <Grid key={col.accessorKey} item xs={12}>
                        <InputCst
                          key={col.accessorKey}
                          id={col.accessorKey}
                          label={col.header}
                          value={state[col.accessorKey]}
                          required={col.required}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: col.accessorKey,
                                value: e.target.value,
                              },
                            });
                          }}
                          maxValue={col?.rules?.max?.value}
                          multiline={col.multiline}
                          rows={col.rows}
                          disabled={col.disabled}
                          textTooltip={col.tooltip}
                          type={col.inpuType || "text"}
                          error={!!errors[col.accessorKey]}
                          errorMessage={`${t(`validation.${errors[col.accessorKey]}`)}`}
                        />
                      </Grid>
                    );
                  case "title":
                    return (
                      <Grid key={col.accessorKey} item xs={12}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            marginBottom: "16px",
                            marginTop: "16px",
                            color: theme.palette.text.primary,
                          }}
                        >
                          {col.header}
                        </Typography>
                      </Grid>
                    );
                  case "multi-select":
                    return (
                      <Grid key={col.accessorKey} item xs={12} sm={6}>
                        <MultiSelectCst
                          key={col.accessorKey}
                          id={col.accessorKey}
                          label={col.header}
                          value={state[col.accessorKey] ?? ""}
                          onChange={(e: any) =>
                            handleChangeMultiSelect(e, col.accessorKey)
                          }
                          options={getOptions(col.accessorKey)}
                          disabled={
                            col.accessorKey === "knowledge_domain" &&
                            (!state["knowledge_area"] ||
                              state["knowledge_area"].length === 0 ||
                              knowledgeDomain.length === 0)
                          }
                        />
                      </Grid>
                    );
                  case "select":
                    return (
                      <Grid key={col.accessorKey} item xs={12} sm={6}>
                        <SelectCst
                          key={col.accessorKey}
                          id={col.accessorKey}
                          label={col.header}
                          value={state[col.accessorKey] ?? ""}
                          onChange={(e: any) =>
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: { key: col.accessorKey, value: e },
                            })
                          }
                          options={getOptions(col.accessorKey)}
                        />
                      </Grid>
                    );
                  case "replaced-course":
                    return (
                      <Grid key={col.accessorKey} item xs={12}>
                        <ReplacedCourseField
                          corporateId={String(id ?? "")}
                          label={col.header}
                          enabled={!!state.replacesPlatformCourse}
                          selected={state.replacedCourse ?? null}
                          error={
                            errors[col.accessorKey]
                              ? String(
                                  t(`validation.${errors[col.accessorKey]}`),
                                )
                              : undefined
                          }
                          onToggle={(next) =>
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: "replacesPlatformCourse",
                                value: next,
                              },
                            })
                          }
                          onSelect={(next) =>
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: { key: "replacedCourse", value: next },
                            })
                          }
                        />
                      </Grid>
                    );
                  case "groups":
                    return (
                      <Grid key={col.accessorKey} item xs={12}>
                        <InputGroup
                          label={col.header}
                          fields={[
                            {
                              label: "Knowledge Area",
                              accessorKey: "knowledge_area",
                              type: "select",
                              labelHeader: {
                                en: "Knowledge Area",
                                it: "Area di Conoscenza",
                              },
                              options: getOptions("knowledge_area"),
                              required: false,
                            },
                            {
                              label: "Knowledge Domain",
                              accessorKey: "knowledge_domain",
                              type: "select",
                              labelHeader: {
                                en: "Knowledge Domain",
                                it: "Dominio di Conoscenza",
                              },
                              options: getOptions("knowledge_domain"),
                              dependentOn: "knowledge_area",
                              required: false,
                            },
                          ]}
                          onChange={(newValue) =>
                            handleFieldChange(col.accessorKey, newValue)
                          }
                          value={state[col.accessorKey] || []}
                          onFieldChange={(e) => setSelectedArea({ value: e })}
                        />
                      </Grid>
                    );
                  case "file":
                    return (
                      <Grid key={col.accessorKey} item xs={12} sm={6}>
                        <label htmlFor={col.accessorKey}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              border: `1px dashed ${theme.palette.divider}`,
                              paddingLeft: theme.spacing(3.3),
                              paddingRight: theme.spacing(0.3),
                              cursor: "pointer",
                              position: "relative",
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <Typography variant="body1" component="span">
                              {state.certificate
                                ? `${t("add.inputs.certificate_uploaded")} ${
                                    state.file_name
                                  }`
                                : col.header}
                            </Typography>
                            <IconButton component="span" sx={{ marginLeft: 1 }}>
                              <UploadIcon />
                            </IconButton>
                            <VisuallyHiddenInput
                              id={col.accessorKey}
                              type="file"
                              accept=".pdf, .jpg, .jpeg, .png"
                              onChange={(e: any) =>
                                handleFileChange(
                                  e,
                                  col.accessorKey,
                                  dispatch,
                                  MAX_SIZE_UPLOAD,
                                  1024,
                                )
                              }
                              error={!!state["error_file"]}
                              errorMessage={`${t(
                                `validation.${state["error_file"]}`,
                                { maxSize: bytesToMB(MAX_SIZE_UPLOAD) },
                              )}`}
                            />
                          </Box>
                        </label>
                      </Grid>
                    );
                  case "multiFile":
                    return (
                      <Grid key={col.accessorKey} item xs={12} sm={12}>
                        <label htmlFor={col.accessorKey}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              border: `1px dashed ${theme.palette.divider}`,
                              paddingLeft: theme.spacing(3.3),
                              paddingRight: theme.spacing(0.3),
                              cursor: "pointer",
                              position: "relative",
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <Typography variant="body1" component="span">
                              {col.header}
                            </Typography>
                            <IconButton component="span" sx={{ marginLeft: 1 }}>
                              <UploadIcon />
                            </IconButton>
                            <VisuallyHiddenInput
                              id={col.accessorKey}
                              type="file"
                              accept=".pdf, .jpg, .jpeg, .png, .csv, .xls, .xlsx, .doc, .docx"
                              multiple
                              onChange={(e: any) =>
                                handleMultiFileChange(
                                  e,
                                  col.accessorKey,
                                  dispatch,
                                  MAX_SIZE_UPLOAD,
                                  1024,
                                  state.attachments,
                                )
                              }
                              error={!!state["error_file_attachments"]}
                              errorMessage={`${t(
                                `validation.${state["error_file_attachments"]}`,
                                { maxSize: bytesToMB(MAX_SIZE_UPLOAD) },
                              )}`}
                            />
                          </Box>
                        </label>
                        {state?.attachments?.length > 0 && (
                          <List dense>
                            {state?.attachments?.map((file: any) => (
                              <ListItem
                                key={file.name}
                                secondaryAction={
                                  <>
                                    <IconButton
                                      edge="end"
                                      aria-label="open"
                                      onClick={() => {
                                        handleDownloadFile(file);
                                      }}
                                    >
                                      <FileDownloadIcon />
                                    </IconButton>

                                    <IconButton
                                      edge="end"
                                      aria-label="delete"
                                      onClick={() =>
                                        handleMultiFileDelete(
                                          col.accessorKey,
                                          dispatch,
                                          file.name,
                                          state.attachments,
                                        )
                                      }
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </>
                                }
                              >
                                <ListItemText primary={file.name} />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Grid>
                    );
                  case "customFields":
                    const customFieldsData = state[col.accessorKey] || [];

                    return (
                      <Grid key={col.accessorKey} item xs={12}>
                        {customFieldsData?.length > 0 && (
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              marginBottom: "16px",
                              marginTop: "16px",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {t("other_info")}
                          </Typography>
                        )}
                        {customFieldsData?.length > 0 ? (
                          <Grid container spacing={3}>
                            {customFieldsData.map(
                              (field: any, index: number) => (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  key={field.fieldId || index}
                                >
                                  <InputCst
                                    id={`${col.accessorKey}_${field.fieldId}`}
                                    label={
                                      field.fieldName || `Field ${index + 1}`
                                    }
                                    value={field.fieldValue || ""}
                                    onChange={(e: any) => {
                                      const updatedFields = [
                                        ...customFieldsData,
                                      ];
                                      updatedFields[index] = {
                                        ...updatedFields[index],
                                        fieldValue: e.target.value,
                                      };

                                      dispatch({
                                        type: "SET_INPUT_VALUES",
                                        payload: {
                                          key: col.accessorKey,
                                          value: updatedFields,
                                        },
                                      });
                                    }}
                                    maxValue={col?.rules?.max?.value}
                                    multiline={col.multiline}
                                    rows={col.rows}
                                    error={
                                      !!errors[
                                        `${col.accessorKey}_${field.fieldId}`
                                      ]
                                    }
                                    errorMessage={`${t(
                                      `validation.${
                                        errors[
                                          `${col.accessorKey}_${field.fieldId}`
                                        ]
                                      }`,
                                    )}`}
                                    disabled={col.disabled}
                                    textTooltip={col.tooltip}
                                    type={col.inpuType || "text"}
                                  />
                                </Grid>
                              ),
                            )}
                          </Grid>
                        ) : null}
                      </Grid>
                    );
                  default:
                    return <></>;
                }
              })}
          </Grid>
        </Box>
      </Grid>
    </>
  );
};

export default Step2;
